import {
	DEFAULT_FIRING,
	type MpLeague,
	type MpMember,
} from "../../common/multiplayerConfig.ts";
import { uniform } from "../../common/random.ts";
import { league, team } from "../core/index.ts";
import { idb } from "../db/index.ts";
import { g, helpers, logEvent } from "../util/index.ts";
import { getClient, getLobbyState, getSession } from "./index.ts";

// ============================================================================
// Phase 7: commissioner rules. At each season-end the runner evaluates the
// firing conditions for every human GM over their tenure on the current team.
// Firings are staged (the commissioner gets the offseason to override any),
// then applied as events that reassign the GM - with a small chance of
// "failing upward" to a good team instead of dropping to a bad one.
// ============================================================================

const sumValues = (obj: Record<string, number>) =>
	Object.values(obj).reduce((a, b) => a + b, 0);

type TeamSeasonLite = {
	tid: number;
	season: number;
	won: number;
	lost: number;
	profit: number;
	playoffRoundsWon: number;
};

export type FiringInfo = {
	userId: string;
	tid: number;
	reason: string;
};

export type HotSeatInfo = {
	tid: number;
	message: string;
};

const firingSettings = (settings: MpLeague["settings"]) => ({
	autoFiring: settings.autoFiring ?? DEFAULT_FIRING.autoFiring,
	noProfitYears: settings.noProfitYears ?? DEFAULT_FIRING.noProfitYears,
	noPlayoffsYears: settings.noPlayoffsYears ?? DEFAULT_FIRING.noPlayoffsYears,
	noConfFinalsYears:
		settings.noConfFinalsYears ?? DEFAULT_FIRING.noConfFinalsYears,
	failUpwardPct: helpers.bound(
		settings.failUpwardPct ?? DEFAULT_FIRING.failUpwardPct,
		0,
		1,
	),
});

// The completed teamSeasons, as lightweight rows with profit precomputed
export const loadCompletedSeasons = async (): Promise<TeamSeasonLite[]> => {
	const currentSeason = g.get("season");
	const rows = await idb.cache.teamSeasons.getAll();
	return rows
		.filter((ts) => ts.season < currentSeason)
		.map((ts) => ({
			tid: ts.tid,
			season: ts.season,
			won: ts.won,
			lost: ts.lost,
			profit: sumValues(ts.revenues) - sumValues(ts.expenses),
			playoffRoundsWon: ts.playoffRoundsWon,
		}));
};

// Most-recent tenure seasons for a team, newest first
const tenureSeasons = (
	all: TeamSeasonLite[],
	tid: number,
	tenureStart: number,
) =>
	all
		.filter((ts) => ts.tid === tid && ts.season >= tenureStart)
		.sort((a, b) => b.season - a.season);

const effectiveTenureStart = (
	member: MpMember & { tenure_start_season?: number | null },
) => member.tenure_start_season ?? g.get("startingSeason");

// ============================================================================
// Evaluation
// ============================================================================

export const evaluateFirings = (
	members: (MpMember & { tenure_start_season?: number | null })[],
	all: TeamSeasonLite[],
	settings: MpLeague["settings"],
): {
	firings: (FiringInfo & { reason: string })[];
	hotSeats: HotSeatInfo[];
} => {
	const cfg = firingSettings(settings);
	const numRounds = g.get("numGamesPlayoffSeries", "current").length;
	const confFinalsRoundsWon = Math.max(0, numRounds - 2);

	const firings: (FiringInfo & { reason: string })[] = [];
	const hotSeats: HotSeatInfo[] = [];
	if (!cfg.autoFiring) {
		return { firings, hotSeats };
	}

	for (const member of members) {
		if (member.tid === null) {
			continue;
		}
		const seasons = tenureSeasons(
			all,
			member.tid,
			effectiveTenureStart(member),
		);
		if (seasons.length === 0) {
			continue;
		}

		// Consecutive most-recent unprofitable seasons
		let profitStreak = 0;
		for (const s of seasons) {
			if (s.profit <= 0) {
				profitStreak += 1;
			} else {
				break;
			}
		}

		let reason: string | undefined;
		if (profitStreak >= cfg.noProfitYears) {
			reason = `${cfg.noProfitYears} straight seasons without a profit`;
		} else if (
			seasons.length >= cfg.noPlayoffsYears &&
			seasons.slice(0, cfg.noPlayoffsYears).every((s) => s.playoffRoundsWon < 0)
		) {
			reason = `no playoff appearance in ${cfg.noPlayoffsYears} seasons`;
		} else if (
			seasons.length >= cfg.noConfFinalsYears &&
			seasons
				.slice(0, cfg.noConfFinalsYears)
				.every((s) => s.playoffRoundsWon < confFinalsRoundsWon)
		) {
			reason = `no conference finals in ${cfg.noConfFinalsYears} seasons`;
		}

		if (reason) {
			firings.push({ userId: member.user_id, tid: member.tid, reason });
		} else if (profitStreak >= cfg.noProfitYears - 2 && profitStreak > 0) {
			// Hot seat in the lead-up years to a profit firing
			hotSeats.push({
				tid: member.tid,
				message: `On the hot seat: ${profitStreak} straight seasons without a profit (fired at ${cfg.noProfitYears}).`,
			});
		}
	}

	return { firings, hotSeats };
};

// ============================================================================
// Reassignment (the ~12% fail-upward roll happens here, once, at staging)
// ============================================================================

const inBand = (pop: number, refPop: number, bandFactor = 2) =>
	pop >= refPop / bandFactor && pop <= refPop * bandFactor;

// Pick the new team for a fired GM. ~failUpwardPct of the time they "fail
// upward" to a top-4-by-record AI team; otherwise they drop to the worst AI
// team. Prefer teams in the fired GM's market band, with fallbacks so a team
// is always found.
export const reassignTeam = async (
	firedTid: number,
	humanTids: number[],
	completedSeasons: TeamSeasonLite[],
	failUpwardPct: number,
): Promise<{ newTid: number; failUpward: boolean }> => {
	const teams = await idb.cache.teams.getAll();
	const popByTid = new Map(teams.map((t) => [t.tid, t.pop ?? 1]));
	const refPop = popByTid.get(firedTid) ?? 1;

	const latestSeason = Math.max(...completedSeasons.map((s) => s.season));
	const recordByTid = new Map(
		completedSeasons
			.filter((s) => s.season === latestSeason)
			.map((s) => [s.tid, s.won - s.lost]),
	);

	// AI teams available to reassign to (not human-controlled, not the vacated
	// team, not disabled)
	const humanSet = new Set(humanTids);
	const available = teams.filter(
		(t) => !t.disabled && t.tid !== firedTid && !humanSet.has(t.tid),
	);

	const byRecord = [...available].sort(
		(a, b) => (recordByTid.get(b.tid) ?? 0) - (recordByTid.get(a.tid) ?? 0),
	);

	const failUpward = uniform(0, 1) < failUpwardPct;

	const pickInBand = (candidates: typeof available) => {
		const banded = candidates.filter((t) =>
			inBand(popByTid.get(t.tid) ?? 1, refPop),
		);
		return banded.length > 0 ? banded : candidates;
	};

	let newTid: number;
	if (failUpward) {
		// Top 4 by record, prefer in-band, fallback to best available
		const top4 = byRecord.slice(0, 4);
		const pool = pickInBand(top4.length > 0 ? top4 : byRecord);
		newTid = pool[0]?.tid ?? byRecord[0]?.tid ?? firedTid;
	} else {
		// Worst by record, prefer in-band, fallback to worst available
		const worst = [...byRecord].reverse();
		const pool = pickInBand(worst);
		newTid = pool[0]?.tid ?? worst[0]?.tid ?? firedTid;
	}

	return { newTid, failUpward };
};

// ============================================================================
// Event application (deterministic on every client)
// ============================================================================

const myUserId = async () => {
	const session = await getSession();
	return session?.userId;
};

// A GM was fired and reassigned: the vacated team goes AI, the new team is now
// human-controlled. If I'm the fired GM, my controlled team changes too.
export const applyFiringEvent = async (payload: {
	user_id: string;
	old_tid: number;
	new_tid: number;
	reason: string;
	fail_upward: boolean;
	season: number;
}) => {
	const userTids = g.get("userTids").filter((tid) => tid !== payload.old_tid);
	if (!userTids.includes(payload.new_tid)) {
		userTids.push(payload.new_tid);
	}
	userTids.sort((a, b) => a - b);

	const attrs: Record<string, unknown> = { userTids };
	if ((await myUserId()) === payload.user_id) {
		attrs.userTid = payload.new_tid;
	}
	await league.setGameAttributes(attrs);
	await league.updateMeta();

	// Keep both teams' rosters legally sorted under their new control status
	await team.rosterAutoSort(payload.old_tid);
	await team.rosterAutoSort(payload.new_tid);

	const teamName = (tid: number) => {
		const t = g.get("teamInfoCache")[tid];
		return t ? `${t.region} ${t.name}` : `Team ${tid}`;
	};
	logEvent({
		type: "info",
		text: `The <b>${teamName(payload.old_tid)}</b> fired their GM (${payload.reason}). ${
			payload.fail_upward ? "Landing on their feet, they" : "They"
		} took over the <b>${teamName(payload.new_tid)}</b>.`,
		saveToDb: false,
		showNotification: true,
	});
};

// ============================================================================
// Runner: stage firings at the offseason, apply them before the next sim
// ============================================================================

export const stageFirings = async (leagueId: string) => {
	const { league: mpLeague, members } = await getLobbyState(leagueId);
	const cfg = firingSettings(mpLeague.settings);
	if (!cfg.autoFiring) {
		return { staged: 0 };
	}

	const completed = await loadCompletedSeasons();
	if (completed.length === 0) {
		return { staged: 0 };
	}

	const { firings } = evaluateFirings(
		members as any,
		completed,
		mpLeague.settings,
	);
	if (firings.length === 0) {
		await getClient().rpc("stage_firings", {
			p_league_id: leagueId,
			p_season: g.get("season"),
			p_firings: [],
		});
		return { staged: 0 };
	}

	const humanTids = members
		.map((m) => m.tid)
		.filter((tid): tid is number => tid !== null);

	// Reassign each fired GM. The vacated team frees up for others, so update
	// the human set as we go.
	const staged: any[] = [];
	const humanSet = new Set(humanTids);
	for (const f of firings) {
		const others = [...humanSet].filter((tid) => tid !== f.tid);
		const { newTid, failUpward } = await reassignTeam(
			f.tid,
			others,
			completed,
			cfg.failUpwardPct,
		);
		humanSet.delete(f.tid);
		humanSet.add(newTid);
		staged.push({
			user_id: f.userId,
			tid: f.tid,
			new_tid: newTid,
			reason: f.reason,
			fail_upward: failUpward,
			season: g.get("season"),
		});
	}

	await getClient().rpc("stage_firings", {
		p_league_id: leagueId,
		p_season: g.get("season"),
		p_firings: staged,
	});
	return { staged: staged.length };
};

// Apply any surviving pending firings (creates the events + reassigns), called
// by the runner just before simming the next season.
export const applyPendingFirings = async (leagueId: string) => {
	const { data } = await getClient()
		.from("pending_firings")
		.select()
		.eq("league_id", leagueId);
	const pending = data ?? [];
	if (pending.length === 0) {
		return { applied: 0 };
	}

	const { error } = await getClient().rpc("apply_firings", {
		p_league_id: leagueId,
		p_events: [],
	});
	if (error) {
		throw new Error(error.message);
	}
	return { applied: pending.length };
};
